/**
 * DataLoadError — Reusable inline error state for data-loading failures.
 *
 * Renders an AlertCallout (error variant) with an optional entity name for
 * contextual messaging and a "Try Again" button that calls onRetry.
 *
 * Per DR-12, icons are allowed inside AlertCallout — no violation here.
 * All colors use semantic tokens only (no hardcoded Tailwind colors).
 */
import { AlertCallout } from "@/components/ui/alert-callout";
import { Button } from "@/components/ui/button";
import { StackLayout } from "@/design-system/salt";

interface DataLoadErrorProps {
  /** The raw Error object or null (message is extracted automatically). */
  error?: Error | null;
  /** Callback invoked when the user clicks "Try Again". Typically React Query's refetch(). */
  onRetry: () => void;
  /**
   * Human-readable entity name shown in the error message, e.g. "goals", "vendors",
   * "contacts". Defaults to "data".
   */
  entityName?: string;
}

/** Data Load Error component. */
export function DataLoadError({ error, onRetry, entityName = "data" }: DataLoadErrorProps) {
  const detail = error?.message;

  return (
    <AlertCallout
      variant="error"
      title={`Failed to load ${entityName}`}
    >
      <StackLayout direction="column" gap={1.5}>
        <p>
          {detail
            ? detail
            : `Unable to load ${entityName}. This may be a temporary issue — please try again.`}
        </p>
        <Button variant="outline" onClick={onRetry}>
          Try Again
        </Button>
      </StackLayout>
    </AlertCallout>
  );
}
